import React, {useEffect, useLayoutEffect, useState} from 'react';
import {SafeAreaView, View, ActivityIndicator} from 'react-native';
import moment from 'moment';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import {
  dealer as dealerApi,
  service_request as serviceRequestApi,
} from '~/api/public';
import {
  Pressable,
  Text,
  ServiceRequestStatusIcon,
  Grouping,
  PrimaryButton,
} from '~/components/controls';
import {Row as GroupingRow} from '~/components/controls/Grouping';
import {PublicContentView} from '~/components/shared';
import {
  TaskActionsheet,
  TiresActionsheet,
} from '~/components/shared/Actionsheets';
import Format from '~/lib/format';
import {useDispatch} from '~/lib/hooks';
import {setDealer} from '~/actions';
import {STATUS_DESCRIPTIONS} from '~/types/statuses';

import ChevronLeftIcon from '~/images/md-icons/chevron_left/materialicons/24px.svg';
import ThumbUpIcon from '~/images/md-icons/thumb_up/materialicons/24px.svg';
import ThumbDownIcon from '~/images/md-icons/thumb_down/materialicons/24px.svg';
import RecommendIcon from '~/images/md-icons/recommend/materialsymbolsoutlined/24px.svg';
import EventIcon from '~/images/md-icons/event/materialicons/24px.svg';
import DirectionsCarIcon from '~/images/md-icons/directions_car/materialicons/24px.svg';
import BusinessIcon from '~/images/md-icons/business/materialicons/24px.svg';
import AssignmentIcon from '~/images/md-icons/assignment/materialicons/24px.svg';
import {STATUS_CODES} from '~/types/statuses';

const TIRE_REPAIR = {
  Repareren: 'Repareren',
  Balanceren: 'Balanceren',
  Wielomwisselen: 'Wielomwisselen',
};
export const TASK_STATUS = {
  Approved: 'Approved',
  Denied: 'Denied',
};

const TaskSubtitle = ({component, is_tires_item, enabled}) => {
  let content = <></>;
  if (is_tires_item) {
    const tire_actions = (component.parts ?? []).flatMap((part) => {
      switch (part.action) {
        case 'Current_TireRepair':
          return part.repairs.map((item) =>
            item === TIRE_REPAIR.Wielomwisselen ? 'Omwisselen' : item,
          );
        case 'Current_Replace':
          return ['Bandvervanging'];
        case 'Current_Store':
        case 'Current_Remove':
          return ['Wissel zonder velg'];
        case 'Current_StoreWheel':
          return ['Wissel met velg'];

        default:
          break;
      }

      return [];
    });

    content = [...new Set(tire_actions ?? [])].join(', ');
  } else {
    content = component.work_text ?? component.description;
  }

  return (
    <Text
      style={[
        sy.truncate,
        sy.small,
        sy['text-lightgray'],
        enabled && sy['underline'],
      ]}>
      {content}
    </Text>
  );
};

const Footer = ({status, amount, deniedTasks, onDoRequest}) => {
  let submitButton = <></>;
  const disabled = status !== STATUS_CODES.ApprovalRequested;

  if (deniedTasks.length > 0) {
    submitButton = (
      <PrimaryButton
        disabled={disabled}
        onPress={onDoRequest}
        color="#972727"
        icon={() => (
          <ServiceRequestStatusIcon
            status={STATUS_CODES.Denied}
            fill="#FFFFFF"
            iconProps={{
              width: 18,
              height: 18,
            }}
          />
        )}>
        Afkeuren
      </PrimaryButton>
    );
  } else {
    submitButton = (
      <PrimaryButton
        disabled={disabled}
        onPress={onDoRequest}
        color="#299976"
        icon={() => (
          <ServiceRequestStatusIcon
            status={STATUS_CODES.Approved}
            fill="#FFFFFF"
            iconProps={{
              width: 18,
              height: 18,
            }}
          />
        )}>
        Goedkeuren
      </PrimaryButton>
    );
  }

  return (
    status !== STATUS_CODES.Invoiced && (
      <View style={[sy.footerView, sy['justify-between'], sy['items-center']]}>
        <Text style={sy.mediumPlus}>{Format.price(amount)}</Text>
        <View style={[sy['flex-row'], sy['items-center']]}>{submitButton}</View>
      </View>
    )
  );
};

const Overview = ({route, navigation}) => {
  const dispatch = useDispatch();
  const request_id = route.params?.request_id;
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [editable, setEditable] = useState(false);
  const [deniedTasks, setDeniedTasks] = useState([]);
  const [approvedTasks, setApprovedTasks] = useState([]);
  const [showTaskEdit, setShowTaskEdit] = useState(null);
  const [showTiresEdit, setShowTiresEdit] = useState(null);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerLeft: () => (
        <Pressable
          style={{paddingLeft: 12}}
          onPress={() => {
            navigation.navigate('Chat', {request_id});
          }}>
          <ChevronLeftIcon />
        </Pressable>
      ),
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerTitle: () =>
        loading ? null : (
          <View>
            <Text style={sy.largePlus}>
              {Format.license(overview?.license)}
            </Text>
          </View>
        ),
      headerRight: () =>
        loading ? null : (
          <ServiceRequestStatusIcon
            style={{marginRight: 16}}
            status={overview?.status}
          />
        ),
    });
  }, [navigation, request_id, loading, overview]);

  useEffect(() => {
    const fetch = async () => {
      const overview = await serviceRequestApi.overview(request_id);

      const dealer = await dealerApi.details(overview.dealer_id);
      dispatch(setDealer(dealer));

      setOverview(overview);
      setEditable(overview.status === STATUS_CODES.ApprovalRequested);
      setLoading(false);
    };

    fetch();
  }, [request_id, navigation, dispatch]);

  const onDoRequest = async () => {
    const approved = overview.components
      .map((component) => {
        if (deniedTasks?.includes(component.id)) {
          return null;
        }

        return component.id;
      }, [])
      .filter(Boolean);

    const {success} = await serviceRequestApi.confirm(
      request_id,
      overview.dealer_request_id,
      approved,
    );

    if (success) {
      navigation.navigate('Chat', {request_id});
    }
  };

  const onTaskChange = (component_id, task_denied) => {
    let denied = deniedTasks.filter((id) => id !== component_id);
    let approved = approvedTasks.filter((id) => id !== component_id);

    if (task_denied) {
      denied = [...denied, component_id];
    } else {
      approved = [...approved, component_id];
    }

    setDeniedTasks(denied);
    setApprovedTasks(approved);
  };

  const total_amount = overview?.components?.reduce(
    (total, component) => total + component.task_amount,
    0,
  );

  return (
    <>
      <SafeAreaView style={sy.mainView}>
        <Divider />
        {loading && <ActivityIndicator size="large" color="#231fda" />}
        {!loading && (
          <PublicContentView>
            {{
              content: (
                <>
                  <Grouping
                    icon={<EventIcon fill="#4A4A49" />}
                    header={
                      <>
                        <Text style={[sy.mediumPlus, sy.capitalize]}>
                          {moment(overview?.datetime, 'YYYY-MM-DD').format(
                            'dddd',
                          )}
                        </Text>
                        &nbsp;
                        {moment(overview?.datetime, 'YYYY-MM-DD').format(
                          'D MMMM',
                        )}
                        &nbsp;&middot;&nbsp;
                        {moment.utc(overview?.datetime).format('H:mm')}
                        &nbsp;uur
                      </>
                    }
                    options={{topMost: true}}>
                    <GroupingRow title="Vestiging">
                      <Text>{overview?.dealer_city}</Text>
                    </GroupingRow>
                    <GroupingRow title="Datum">
                      <Text>
                        {moment(overview?.datetime, 'YYYY-MM-DD').format(
                          'dddd D MMMM YYYY',
                        )}
                      </Text>
                    </GroupingRow>
                    <GroupingRow title="Tijd">
                      <Text>
                        {moment.utc(overview?.datetime).format('H:mm')}
                      </Text>
                    </GroupingRow>
                  </Grouping>

                  <Grouping
                    icon={<DirectionsCarIcon fill="#4A4A49" />}
                    header={overview?.car_name}>
                    <GroupingRow title="Brandstof">
                      <Text>{overview?.fuel}</Text>
                    </GroupingRow>
                    <GroupingRow title="APK vervaldatum">
                      <Text>
                        {moment(overview?.car_apk_expiry).format('D-M-YYYY')}
                      </Text>
                    </GroupingRow>
                    <GroupingRow title="Kilometerstand">
                      <Text>
                        {Format.number(overview?.mileage, 0, true) || (
                          <>Niet opgegeven</>
                        )}
                      </Text>
                    </GroupingRow>
                    <GroupingRow title="Eerste toelating">
                      <Text>
                        {moment(overview?.car_entry_date).format('D-M-YYYY')}
                        &nbsp;(
                        {Format.number(
                          moment().diff(
                            moment(overview?.car_entry_date),
                            'months',
                          ) / 12,
                          1,
                          false,
                          {minimumFractionDigits: 0},
                        )}
                        &nbsp;jaar)
                      </Text>
                    </GroupingRow>
                  </Grouping>

                  <Grouping
                    icon={<BusinessIcon fill={'#4A4A49'} />}
                    header={
                      (['Zakelijk', 'Particulier'].includes(overview?.lessor)
                        ? overview?.kvk_name ?? overview?.client
                        : null) ?? overview?.lessor
                    }>
                    <GroupingRow title="Berijder">
                      <Text>{overview?.client || <>Niet oppgeven</>}</Text>
                    </GroupingRow>
                    <GroupingRow title="Mobiele telefoon">
                      <Text>
                        {overview?.phone_number || <>Niet opgegeven</>}
                      </Text>
                    </GroupingRow>
                    <GroupingRow title="E-mail">
                      <Text>{overview?.email || <>Niet opgegeven</>}</Text>
                    </GroupingRow>
                  </Grouping>

                  <View>
                    <Divider />
                    <View
                      style={[
                        sy['flex-row'],
                        sy['items-center'],
                        sy['justify-between'],
                        sy['p-4'],
                      ]}>
                      <View style={[sy['flex-row'], sy['flex-1']]}>
                        <View style={[sy['pr-8']]}>
                          <AssignmentIcon fill="#4A4A49" />
                        </View>
                        <Text style={sy.mediumPlus}>
                          Opdracht&nbsp;&middot;&nbsp;
                          {STATUS_DESCRIPTIONS[overview.status]}
                        </Text>
                      </View>
                      <Text style={sy.mediumPlus}>
                        {Format.price(total_amount)}
                      </Text>
                    </View>
                    <Divider />
                    {overview?.components
                      ?.sort((component) => component.name)
                      .map((component) => {
                        const onTaskPress = () => {
                          if (component.name === 'Banden') {
                            setShowTiresEdit({component});
                          } else {
                            setShowTaskEdit({
                              component,
                            });
                          }
                        };

                        return (
                          <React.Fragment key={component.id}>
                            {
                              <>
                                <View
                                  style={[
                                    sy['flex-row'],
                                    sy['flex-1'],
                                    sy['p-4'],
                                    sy['gap-4'],
                                    !editable && sy['view-disabled'],
                                  ]}>
                                  <Pressable
                                    style={[
                                      sy['justify-start'],
                                      sy['pr-4'],
                                      {width: 'auto'},
                                    ]}
                                    disabled={!editable}
                                    onPress={onTaskPress}>
                                    {component.status?.code ===
                                      TASK_STATUS.Denied ||
                                    deniedTasks.includes(component.id) ? (
                                      <ThumbDownIcon fill="#972727" />
                                    ) : approvedTasks.includes(component.id) ||
                                      component.status?.code ===
                                        TASK_STATUS.Approved ? (
                                      <ThumbUpIcon fill="#299976" />
                                    ) : (
                                      <RecommendIcon fill="#2907E3" />
                                    )}
                                  </Pressable>
                                  <View style={[sy['flex-1']]}>
                                    <Pressable
                                      disabled={!editable}
                                      onPress={onTaskPress}>
                                      <View
                                        style={[
                                          sy['flex-row'],
                                          sy['justify-between'],
                                        ]}>
                                        <View style={[sy['flex-1']]}>
                                          <View style={[sy['flex-row']]}>
                                            <Text style={[sy.truncate]}>
                                              {component.name}
                                            </Text>
                                          </View>
                                          {!!component && (
                                            <TaskSubtitle
                                              component={component}
                                              is_tires_item={
                                                component.name === 'Banden'
                                              }
                                              enabled={editable}
                                            />
                                          )}
                                        </View>
                                      </View>
                                    </Pressable>
                                  </View>
                                  <View style={[sy['items-end']]}>
                                    <Text style={sy.smallRegular}>
                                      {Format.price(component.task_amount)}
                                    </Text>
                                  </View>
                                </View>
                                <View style={[sy['pl-18']]}>
                                  <Divider
                                    style={[{backgroundColor: '#f2f2f2'}]}
                                  />
                                </View>
                              </>
                            }
                          </React.Fragment>
                        );
                      })}
                  </View>
                </>
              ),
              footer: (
                <Footer
                  status={overview.status}
                  amount={total_amount}
                  deniedTasks={deniedTasks}
                  onDoRequest={onDoRequest}
                />
              ),
            }}
          </PublicContentView>
        )}
      </SafeAreaView>
      {showTaskEdit && (
        <TaskActionsheet
          onDismiss={() => setShowTaskEdit(null)}
          is_denied={deniedTasks.includes(showTaskEdit.component.id)}
          component={showTaskEdit.component}
          is_replacement={showTaskEdit.component.rob_code === '1039'}
          onChange={onTaskChange}
        />
      )}

      {showTiresEdit && (
        <TiresActionsheet
          onDismiss={() => setShowTiresEdit(null)}
          request_id={request_id}
          task={showTiresEdit.component}
          is_denied={deniedTasks.includes(showTiresEdit.component.id)}
          onChange={(denied) =>
            onTaskChange(showTiresEdit.component.id, denied)
          }
        />
      )}
    </>
  );
};

export default Overview;
